import React, { useState } from "react";
import { SkeletonRow } from "../components/Skeleton";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getJobs } from "../api";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { localeForLanguage } from "../utils/language";

const STATUS_COLORS: Record<string, string> = {
  active: "#16a34a",
  draft:  "#d97706",
  closed: "#64748b",
};

const STAGE_META: Array<{ key: string; label: string; color: string }> = [
  { key: "new",         label: "Applied",     color: "#3b82f6" },
  { key: "reviewed",    label: "Reviewed",    color: "#8b5cf6" },
  { key: "shortlisted", label: "Shortlisted", color: "#f59e0b" },
  { key: "hired",       label: "Hired",       color: "#16a34a" },
];

type FilterTab = "all" | "active" | "draft" | "closed";

const inputStyle: React.CSSProperties = {
  padding: "9px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13,
};

export default function JobsPage() {
  const tz = useTenantTimezone();
  const { data: jobs = [], isLoading } = useQuery({ queryKey: ["jobs"], queryFn: getJobs });
  const nav = useNavigate();
  const [filter, setFilter] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const clients = React.useMemo(() => {
    const seen = new Map<string, string>();
    jobs.forEach((j: any) => {
      if (j.client_id != null && j.client) seen.set(String(j.client_id), j.client);
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);

  const byTab = filter === "all" ? jobs : jobs.filter((j: any) => j.status === filter);

  const filtered = byTab.filter((j: any) => {
    if (clientId && String(j.client_id) !== clientId) return false;
    if (search && !String(j.title ?? "").toLowerCase().includes(search.toLowerCase())) return false;
    const postedDate = j.created_at ? String(j.created_at).slice(0, 10) : "";
    if (dateFrom && postedDate && postedDate < dateFrom) return false;
    if (dateTo && postedDate && postedDate > dateTo) return false;
    return true;
  });

  const hasActiveFilters = !!(search || clientId || dateFrom || dateTo);
  const clearFilters = () => { setSearch(""); setClientId(""); setDateFrom(""); setDateTo(""); };

  const tabCounts: Record<FilterTab, number> = {
    all:    jobs.length,
    active: jobs.filter((j: any) => j.status === "active").length,
    draft:  jobs.filter((j: any) => j.status === "draft").length,
    closed: jobs.filter((j: any) => j.status === "closed").length,
  };

  const tabStyle = (tab: FilterTab): React.CSSProperties => ({
    padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 600,
    border: "none", cursor: "pointer",
    background: filter === tab ? "#1e3a8a" : "transparent",
    color:      filter === tab ? "white"   : "#64748b",
    transition: "background 0.15s",
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: "#0f172a", fontSize: 24 }}>Job Openings</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>
            {hasActiveFilters
              ? `${filtered.length} of ${byTab.length} position${byTab.length !== 1 ? "s" : ""}`
              : `${filtered.length} ${filter === "all" ? "total" : filter} position${filtered.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <button onClick={() => nav("/jobs/new")} style={{
          background: "#1e3a8a", color: "white", border: "none", padding: "10px 20px",
          borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
        }}>+ New Job</button>
      </div>

      {/* Filter tabs */}
      <div style={{
        display: "flex", gap: 4, marginBottom: 16,
        background: "#f1f5f9", borderRadius: 24, padding: 4,
        width: "fit-content",
      }}>
        {(["all", "active", "draft", "closed"] as FilterTab[]).map(tab => (
          tabCounts[tab] > 0 || tab === "all" ? (
            <button key={tab} style={tabStyle(tab)} onClick={() => setFilter(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tabCounts[tab] > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11,
                  background: filter === tab ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                  color:      filter === tab ? "white" : "#475569",
                  borderRadius: 10, padding: "1px 6px",
                }}>
                  {tabCounts[tab]}
                </span>
              )}
            </button>
          ) : null
        ))}
      </div>

      {/* Client / title / date filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search job title…"
          style={{ ...inputStyle, flex: "1 1 200px", minWidth: 0 }}
        />
        <select value={clientId} onChange={e => setClientId(e.target.value)}
          style={{ ...inputStyle, flex: "1 1 160px", minWidth: 0 }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b" }}>
          Posted from
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b" }}>
          to
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </label>
        {hasActiveFilters && (
          <button onClick={clearFilters} style={{
            background: "transparent", border: "none", color: "#1e3a8a",
            fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "9px 4px",
          }}>Clear filters</button>
        )}
      </div>

      {isLoading && <div style={{ display: "grid", gap: 10 }}><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>}

      <div style={{ display: "grid", gap: 12 }}>
        {filtered.map((job: any) => {
          const totalApplicants: number = job.applicants ?? 0;
          const stages: Record<string, number> = job.stages ?? {};
          const pipelineTotal = STAGE_META.reduce((s, m) => s + (stages[m.key] ?? 0), 0);

          return (
            <div key={job.id}
              onClick={() => nav(`/jobs/${job.id}`)}
              className="list-row"
              style={{
                background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
                padding: "16px 20px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(30,58,138,0.1)")}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)")}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 600, fontSize: 16, color: "#1e293b" }} lang={localeForLanguage(job.language)}>{job.title}</div>
                  {job.language && job.language.toLowerCase() !== "english" && (
                    <span style={{
                      background: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff",
                      padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>{job.language}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                  {job.client && <span style={{ marginRight: 12 }}>Client: {job.client}</span>}
                  <span>Posted: {formatDate(job.created_at, "short", tz)}</span>
                </div>

                {/* Pipeline health bar */}
                {pipelineTotal > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", height: 6, borderRadius: 6, overflow: "hidden", background: "#f1f5f9", gap: 1 }}>
                      {STAGE_META.map(m => {
                        const count = stages[m.key] ?? 0;
                        if (!count) return null;
                        const pct = (count / pipelineTotal) * 100;
                        return <div key={m.key} style={{ width: `${pct}%`, background: m.color, minWidth: 4 }} title={`${count} ${m.label}`} />;
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 12, marginTop: 5, flexWrap: "wrap" }}>
                      {STAGE_META.filter(m => (stages[m.key] ?? 0) > 0).map(m => (
                        <span key={m.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#64748b" }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, display: "inline-block", flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: m.color }}>{stages[m.key]}</span>
                          <span>{m.label}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 6 }}>No applicants yet</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 12, alignItems: "center", marginLeft: 16, flexShrink: 0 }}>
                {/* Applicant count — de-emphasised when 0 */}
                <div style={{ textAlign: "center", minWidth: 48 }}>
                  <div style={{
                    fontSize: 22, fontWeight: 700,
                    color: totalApplicants === 0 ? "#cbd5e1" : "#1e3a8a",
                  }}>
                    {totalApplicants}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>applicants</div>
                </div>

                <span style={{
                  background: STATUS_COLORS[job.status] + "15",
                  color: STATUS_COLORS[job.status],
                  border: `1px solid ${STATUS_COLORS[job.status]}40`,
                  padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                }}>{job.status}</span>

                {job.linkedin_posted && (
                  <span style={{
                    background: "#eff6ff", color: "#1d4ed8",
                    border: "1px solid #bfdbfe",
                    padding: "3px 10px", borderRadius: 20, fontSize: 12,
                  }}>LinkedIn ✓</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isLoading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: "block", margin: "0 auto" }}>
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          </svg>
          <div style={{ fontSize: 15, marginTop: 12, marginBottom: 16 }}>
            {hasActiveFilters
              ? "No jobs match your filters."
              : filter === "all" ? "No job postings yet." : `No ${filter} jobs.`}
          </div>
          {hasActiveFilters ? (
            <button onClick={clearFilters} style={{
              background: "#1e3a8a", color: "white", border: "none", padding: "10px 20px",
              borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
            }}>Clear filters</button>
          ) : filter === "all" && (
            <button onClick={() => nav("/jobs/new")} style={{
              background: "#1e3a8a", color: "white", border: "none", padding: "10px 20px",
              borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
            }}>+ New Job</button>
          )}
        </div>
      )}
    </div>
  );
}
